import { useQuery } from '@tanstack/react-query';
import { apiFetch } from './client';

/**
 * `GET /api/v1/forage/{activityCode}` — the forage spot as a place (docs/SCREENS.md "Forage
 * screen overhaul", server: `world/ForageFindsDto.java`).
 *
 * A per-spot read that only one screen ever wants, kept out of `world.ts` so the board's types
 * never ride into every module that imports the world graph. The `POST` that used to share this
 * path (`useForage`, the 180-minute pull) was retired 2026-09-18 — RULINGS "Retiring the forage
 * pull"; a gather session (`api/skills.ts`) is what turns a cell from not-yet to found now, and
 * `useStartSkill`'s completion path invalidates `forageKeys` for it.
 */

/**
 * One cell of the "What grows here" board. Item-XOR-currency, the same contract `DropDto` carries.
 *
 * `name` is null until the account has actually found this entry here — the server withholds it
 * (`ForageFindsDto.FindDto`'s own javadoc), so an unfound cell has no name to leak even if a
 * future render tried to show one. `itemCode` and `rarity` are always present: the code only
 * resolves to the silhouette the board draws, and rarity is public wherever the item appears.
 */
export type ForageFindDto = {
  itemCode: string | null;
  currency: string | null;
  name: string | null;
  rarity: string | null;
  found: boolean;
};

export type ForageFindsDto = {
  activityCode: string;
  spotName: string;
  landCode: string;
  landName: string;
  landDescription: string | null;
  /** Whether this spot's table has a pity floor AT ALL. A boolean by design — §9.4/C3 keeps pity
   *  "published as existing in the abstract, never numbered", so there is deliberately no counter
   *  and no `within` here to render. */
  pityEnabled: boolean;
  finds: ForageFindDto[];
};

export const forageKeys = {
  all: ['forage'] as const,
  finds: (activityCode: string) => ['forage', 'finds', activityCode] as const,
};

export const getForageFinds = (activityCode: string) =>
  apiFetch<ForageFindsDto>(`/forage/${activityCode}`);

export function useForageFinds(activityCode: string) {
  return useQuery({
    queryKey: forageKeys.finds(activityCode),
    queryFn: () => getForageFinds(activityCode),
    enabled: activityCode !== '',
  });
}
